import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class Tabs implements Plugin {
  private readonly tabButtons: NodeListOf<HTMLElement>;
  private readonly tabContents: NodeListOf<HTMLElement>;
  private readonly activeTabClass: string;
  private readonly activeContentClass: string;
  private readonly inactiveContentClass: string;
  private readonly tabButtonSelector: string;
  private readonly tabContentSelector: string;

  constructor(private readonly element: HTMLElement) {
    // Configuration from data attributes
    this.activeTabClass =
      element.dataset.tabsActiveClass || "dsc-co-tabs__button--active";
    this.activeContentClass =
      element.dataset.tabsActiveContentClass || "dsc-co-tabs__content--active";
    this.inactiveContentClass =
      element.dataset.tabsInactiveContentClass ||
      "dsc-co-tabs__content--inactive";
    this.tabButtonSelector =
      element.dataset.tabsButtonSelector || "[data-tab-button]";
    this.tabContentSelector =
      element.dataset.tabsContentSelector || "[data-tab-content]";

    // Find tab elements
    this.tabButtons = element.querySelectorAll<HTMLElement>(
      this.tabButtonSelector,
    );
    this.tabContents = element.querySelectorAll<HTMLElement>(
      this.tabContentSelector,
    );
  }

  init(): void {
    if (this.tabButtons.length === 0 || this.tabContents.length === 0) {
      console.warn(
        "Tabs plugin: No tab buttons or contents found",
        this.element,
      );
      return;
    }

    this.setupTabs();
    this.initializeFirstTab();
  }

  private setupTabs(): void {
    this.tabButtons.forEach((button, index) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.switchToTab(index);
      });

      // Add keyboard support
      button.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.switchToTab(index);
        }
      });

      // Ensure buttons are focusable
      if (!button.hasAttribute("tabindex")) {
        button.setAttribute("tabindex", "0");
      }
    });
  }

  private initializeFirstTab(): void {
    // Find if there's a tab marked as active, otherwise use the first one
    let activeIndex = 0;
    this.tabButtons.forEach((button, index) => {
      if (button.classList.contains(this.activeTabClass)) {
        activeIndex = index;
      }
    });

    // Clean all tabs first to ensure consistent state
    this.tabButtons.forEach((button) => {
      button.classList.remove(this.activeTabClass);
      button.setAttribute("aria-selected", "false");
    });

    this.tabContents.forEach((content) => {
      if (this.activeContentClass) {
        content.classList.remove(this.activeContentClass);
      }
      content.classList.add(this.inactiveContentClass);
      content.setAttribute("aria-hidden", "true");
    });

    // Then activate the correct tab
    this.switchToTab(activeIndex);
  }

  private switchToTab(targetIndex: number): void {
    if (targetIndex < 0 || targetIndex >= this.tabButtons.length) {
      return;
    }

    // Update tab buttons
    this.tabButtons.forEach((button, index) => {
      if (index === targetIndex) {
        button.classList.add(this.activeTabClass);
        button.setAttribute("aria-selected", "true");
      } else {
        button.classList.remove(this.activeTabClass);
        button.setAttribute("aria-selected", "false");
      }
    });

    // Update tab contents
    this.tabContents.forEach((content, index) => {
      if (index === targetIndex) {
        if (this.activeContentClass) {
          content.classList.add(this.activeContentClass);
        }
        content.classList.remove(this.inactiveContentClass);
        content.setAttribute("aria-hidden", "false");
      } else {
        if (this.activeContentClass) {
          content.classList.remove(this.activeContentClass);
        }
        content.classList.add(this.inactiveContentClass);
        content.setAttribute("aria-hidden", "true");
      }
    });

    // Dispatch custom event
    this.element.dispatchEvent(
      new CustomEvent("tabChanged", {
        detail: {
          activeIndex: targetIndex,
          activeButton: this.tabButtons[targetIndex],
          activeContent: this.tabContents[targetIndex],
        },
      }),
    );
  }

  destroy(): void {
    // Clean up event listeners if needed
    // The plugin registry handles this automatically for most cases
  }
}

PluginRegistry.register("tabs", Tabs);
